/**
 * ReservoirManagementPage.tsx — Reservoir & Lake Management
 *
 * Site-specific management for reservoir, lake, lagoon, and constructed wetland sites:
 *   · Physical characteristics (volume, area, depth, catchment, HRT)
 *   · Aeration systems — standard treatment for reservoirs (surface, diffused, destratification)
 *   · Process train builder — any equipment from the full library
 *   · Water quality & compliance monitoring (TP, TN, Chl-a, DO — sensitive water body limits)
 *   · Trophic state assessment and risk flags
 *
 * Active site is picked from CustomerSite context (passed via router state or localStorage).
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Waves, Plus, Trash2, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle2, Info, Wind,
  Layers, Save, Edit3, X, ArrowLeft,
  Activity, Database, ClipboardList, FileText,
  Filter, Download, RefreshCw, ChevronRight,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import { toast } from 'sonner';
import {
  getLocalSiteProcesses, saveSiteProcess, updateSiteProcess, deleteSiteProcess,
  getReservoirConfig, saveReservoirConfig,
  getSiteCompliance, saveSiteCompliance,
  type CustomerSite, type SiteProcess, type SiteProcessItem,
  type ReservoirConfig, type AerationSystem,
  type ComplianceStatus, type ObligationLevel, type ComplianceOverride, type SiteComplianceData,
} from '@/lib/hybridStorage';

// ─── Equipment library for reservoir / process train ──────────────────────────

interface EquipDef {
  id: string; name: string; category: string; catColor: string;
  purpose: string; energyUse: string; notes: string;
  /** removal fractions for key params (0–1) */
  tpRemoval?: number; tnRemoval?: number; tssRemoval?: number;
  codRemoval?: number; bodRemoval?: number;
  /** DO addition (mg/L per unit per hour — varies by type) */
  doGain?: number;
}

const RESERVOIR_EQUIPMENT: EquipDef[] = [
  // ── Aeration & Mixing ────────────────────────────────────────────────────
  { id: 'surface_aerator',   name: 'Surface Aerator (Floating)',       category: 'Aeration',   catColor: '#06b6d4', purpose: 'Oxygenation, surface mixing',                  energyUse: 'Medium', doGain: 2.0,  tssRemoval: 0.05, notes: 'SAE 1.2–2.4 kg O₂/kWh. Effective in shallow water < 4 m. Creates surface mixing plume.' },
  { id: 'diffused_air',      name: 'Diffused Aeration (Fine Bubble)',  category: 'Aeration',   catColor: '#06b6d4', purpose: 'Deep oxygenation, full-depth mixing',           energyUse: 'Medium', doGain: 2.5,  tssRemoval: 0.05, notes: 'SAE 4–6 kg O₂/kWh. Fine bubble diffusers on lake floor. Effective 3–15 m depth.' },
  { id: 'destratifier',      name: 'Destratification Aerator',        category: 'Aeration',   catColor: '#06b6d4', purpose: 'Break thermal stratification',                  energyUse: 'Low',    doGain: 1.5,  notes: 'Compressed air lifts cold bottom water. Eliminates anoxic hypolimnion. Reduces P release from sediments.' },
  { id: 'draft_tube_mixer',  name: 'Draft Tube Mixer',                 category: 'Aeration',   catColor: '#06b6d4', purpose: 'Vertical circulation, cyanobacteria control',   energyUse: 'Low',    doGain: 1.2,  notes: 'Circulates water downward, preventing cyanobacteria buoyancy. Effective for algal bloom management.' },
  { id: 'solar_aerator',     name: 'Solar-Powered Aerator',           category: 'Aeration',   catColor: '#06b6d4', purpose: 'Autonomous oxygenation, remote locations',       energyUse: 'Very Low', doGain: 0.8, notes: 'No grid power required. Intermittent operation tied to solar irradiance. 1–3 kW typical.' },
  { id: 'fountain_aerator',  name: 'Fountain Aerator',                category: 'Aeration',   catColor: '#06b6d4', purpose: 'Aesthetic aeration, surface mixing',             energyUse: 'Low',    doGain: 0.6,  notes: 'High surface-to-air contact. SAE 0.8–1.5 kg O₂/kWh. Recreational lakes.' },
  { id: 'cascade_aerator',   name: 'Cascade / Weir Aerator',          category: 'Aeration',   catColor: '#06b6d4', purpose: 'Inflow aeration, CO₂ stripping',                energyUse: 'Very Low', doGain: 1.0, notes: 'Gravity-driven. Installed at inflow point. Strips CO₂ and H₂S, adds O₂. No power required.' },
  // ── In-Lake Treatment ───────────────────────────────────────────────────
  { id: 'alum_dosing_lake',  name: 'Alum Dosing (In-Lake)',           category: 'Dosing',     catColor: '#f97316', purpose: 'Phosphorus precipitation, sediment capping',   energyUse: 'Low',    tpRemoval: 0.85, tssRemoval: 0.60, notes: 'Al₂(SO₄)₃ applied to water column or sediments. Binds P as AlPO₄. Controls internal P loading. pH must be 6–8.' },
  { id: 'pac_inlake',        name: 'PAC Dosing (In-Lake)',            category: 'Dosing',     catColor: '#f97316', purpose: 'Phosphorus removal, coagulation',               energyUse: 'Low',    tpRemoval: 0.80, tssRemoval: 0.55, notes: 'Pre-hydrolysed aluminium. Less pH impact than alum. Applied by boat-mounted doser.' },
  { id: 'lanthanum_clay',    name: 'Phoslock® (Lanthanum Clay)',      category: 'Dosing',     catColor: '#f97316', purpose: 'Long-term P binding, sediment treatment',      energyUse: 'Very Low', tpRemoval: 0.90, notes: 'Modified bentonite clay with lanthanum. Settles to sediment surface, seals P release for years.' },
  { id: 'copper_sulphate',   name: 'Copper Sulphate Algaecide',       category: 'Dosing',     catColor: '#f97316', purpose: 'Algae and cyanobacteria control',               energyUse: 'Very Low', notes: 'CuSO₄ applied at 0.1–0.5 mg/L Cu. Licensed for potable reservoirs (some jurisdictions). Repeated applications needed.' },
  { id: 'barley_straw',      name: 'Barley Straw / Biostimulation',   category: 'Dosing',     catColor: '#f97316', purpose: 'Natural algal suppression',                    energyUse: 'Very Low', notes: 'Decomposes to produce H₂O₂-like compounds. Low-cost, low-risk. 6–8 weeks for effect. Limited evidence base.' },
  // ── Inflow Treatment ───────────────────────────────────────────────────
  { id: 'constructed_wetland', name: 'Constructed Wetland (Inflow)', category: 'Pre-Treatment', catColor: '#10b981', purpose: 'Inflow nutrient and sediment polishing',      energyUse: 'Very Low', tpRemoval: 0.60, tnRemoval: 0.55, tssRemoval: 0.80, codRemoval: 0.65, bodRemoval: 0.70, notes: 'Surface flow or sub-surface wetland at inflow. Removes nutrients, sediment, pathogens. HRT 3–7 days.' },
  { id: 'sedimentation_basin', name: 'Inlet Sedimentation Basin',    category: 'Pre-Treatment', catColor: '#10b981', purpose: 'Sediment and nutrient interception at inflow', energyUse: 'Very Low', tpRemoval: 0.35, tssRemoval: 0.75, notes: 'Expanded channel or bay at inflow point. Settles coarse sediment before entering main body.' },
  { id: 'buffer_strip',      name: 'Riparian Buffer / Filter Strip',  category: 'Pre-Treatment', catColor: '#10b981', purpose: 'Diffuse pollution interception',                energyUse: 'Very Low', tpRemoval: 0.40, tnRemoval: 0.45, notes: 'Vegetated strip along catchment margin. Filters runoff. 10–30 m width. Permanent installation.' },
  { id: 'coag_basin',        name: 'Coagulation Basin (Inflow)',      category: 'Pre-Treatment', catColor: '#10b981', purpose: 'Inflow P and turbidity removal',               energyUse: 'Medium', tpRemoval: 0.90, tssRemoval: 0.88, codRemoval: 0.40, notes: 'Inline coagulation/flocculation of inflow stream before entering reservoir. Requires chemical feed.' },
  // ── Offtake Treatment ──────────────────────────────────────────────────
  { id: 'selective_withdrawal', name: 'Selective Withdrawal Structure', category: 'Offtake',  catColor: '#8b5cf6', purpose: 'Abstract water from optimal depth/quality',     energyUse: 'Very Low', notes: 'Multi-level offtake allows operators to select depth with optimal DO, temperature, turbidity. Critical for stratified reservoirs.' },
  { id: 'uv_disinfection',   name: 'UV Disinfection (Offtake)',       category: 'Offtake',    catColor: '#8b5cf6', purpose: 'Pathogen inactivation at offtake',              energyUse: 'Low',    notes: '40 mJ/cm² dose. 4-log Cryptosporidium/Giardia reduction. No residual — combine with downstream Cl₂.' },
  { id: 'cl2_disinfection',  name: 'Chlorination (Offtake)',          category: 'Offtake',    catColor: '#8b5cf6', purpose: 'Residual disinfection of offtake',             energyUse: 'Low',    notes: 'Applied at offtake chamber or WTP. 0.2–0.5 mg/L residual Cl₂ target. DBP formation risk with high DOC.' },
  { id: 'ozone_polish',      name: 'Ozone Polishing (Offtake)',       category: 'Offtake',    catColor: '#8b5cf6', purpose: 'Taste/odour, micropollutant removal',           energyUse: 'High',   notes: '1–4 mg/L O₃. Destroys taste/odour compounds (MIB, geosmin). Requires downstream BAC/GAC filter.' },
  { id: 'gac_filter',        name: 'GAC Filter (Offtake)',            category: 'Offtake',    catColor: '#8b5cf6', purpose: 'Organic carbon and micropollutant removal',     energyUse: 'Low',    codRemoval: 0.50, bodRemoval: 0.60, notes: 'Granular Activated Carbon. Adsorbs DOC, PFAS, pesticides, taste/odour. 15–20 min EBCT.' },
  // ── Monitoring ─────────────────────────────────────────────────────────
  { id: 'multiprobe',        name: 'In-Lake Multiprobe / Sonde',     category: 'Monitoring', catColor: '#64748b', purpose: 'Continuous water quality monitoring',           energyUse: 'Very Low', notes: 'Measures DO, pH, conductivity, turbidity, Chl-a, phycocyanin (cyanobacteria). Telemetry to SCADA.' },
  { id: 'early_warning',     name: 'Algal Early Warning System',     category: 'Monitoring', catColor: '#64748b', purpose: 'Real-time algal bloom detection',              energyUse: 'Very Low', notes: 'Fluorescence sensors for Chl-a and phycocyanin. SMS/email alert on threshold exceedance.' },
  { id: 'sediment_trap',     name: 'Sediment Trap / Mass Balance',   category: 'Monitoring', catColor: '#64748b', purpose: 'Internal P loading quantification',             energyUse: 'Very Low', notes: 'Cylindrical traps measure settling rate and P flux from sediments. Critical for nutrient budget.' },
  // ── Sludge Management ──────────────────────────────────────────────────
  { id: 'dredging',          name: 'Hydraulic Dredging',             category: 'Sludge',     catColor: '#f59e0b', purpose: 'Sediment and internal P removal',              energyUse: 'High',   tpRemoval: 0.70, notes: 'Removes P-rich sediment layer. Long-term solution for internal loading. High cost, requires disposal.' },
  { id: 'sludge_pump',       name: 'Sludge Pump / Desludging',       category: 'Sludge',     catColor: '#f59e0b', purpose: 'Accumulated sludge removal',                   energyUse: 'Medium', notes: 'Submersible pump for sludge blanket removal. Required for water storage reservoirs with high inflow TSS.' },
];

const CATEGORY_ORDER = ['Aeration', 'Pre-Treatment', 'Dosing', 'Offtake', 'Monitoring', 'Sludge'];

const CATEGORY_COLORS: Record<string, string> = {
  'Aeration':      '#06b6d4',
  'Pre-Treatment': '#10b981',
  'Dosing':        '#f97316',
  'Offtake':       '#8b5cf6',
  'Monitoring':    '#64748b',
  'Sludge':        '#f59e0b',
};

// ─── Aeration system types ────────────────────────────────────────────────────

const AERATION_TYPES: { value: AerationSystem['type']; label: string; sae: number; notes: string }[] = [
  { value: 'surface_aerator',  label: 'Surface Aerator (Floating)',  sae: 1.8, notes: 'Typical SAE 1.2–2.4 kg O₂/kWh' },
  { value: 'diffused_air',     label: 'Diffused Air (Fine Bubble)',  sae: 5.0, notes: 'Typical SAE 4–6 kg O₂/kWh' },
  { value: 'destratifier',     label: 'Destratification Aerator',    sae: 2.5, notes: 'Primarily mixing; O₂ transfer secondary' },
  { value: 'solar_aerator',    label: 'Solar-Powered Aerator',       sae: 1.2, notes: 'Intermittent operation' },
  { value: 'draft_tube_mixer', label: 'Draft Tube Mixer',            sae: 1.5, notes: 'Low energy; vertical circulation' },
  { value: 'cascade',          label: 'Cascade / Weir Aerator',      sae: 1.0, notes: 'Gravity-driven; no power' },
  { value: 'fountain',         label: 'Fountain Aerator',            sae: 1.0, notes: 'Aesthetic + aeration' },
];

// ─── Trophic status thresholds ────────────────────────────────────────────────

const TROPHIC: { status: ReservoirConfig['trophicStatus']; label: string; tpRange: string; tnRange: string; chlaRange: string; color: string; risk: string }[] = [
  { status: 'oligotrophic',  label: 'Oligotrophic',  tpRange: '< 0.01',  tnRange: '< 0.35',  chlaRange: '< 2.5',  color: '#06b6d4', risk: 'Low nutrient load. Clear water. Excellent ecological status.' },
  { status: 'mesotrophic',   label: 'Mesotrophic',   tpRange: '0.01–0.03',tnRange: '0.35–0.65',chlaRange: '2.5–8', color: '#10b981', risk: 'Moderate nutrients. Occasional algal growth. Good/Moderate WFD status.' },
  { status: 'eutrophic',     label: 'Eutrophic',     tpRange: '0.03–0.10',tnRange: '0.65–1.2', chlaRange: '8–25',  color: '#f59e0b', risk: 'High nutrients. Algal blooms likely. Poor WFD status. Treatment urgent.' },
  { status: 'hypertrophic',  label: 'Hypertrophic',  tpRange: '> 0.10',  tnRange: '> 1.2',   chlaRange: '> 25',  color: '#ef4444', risk: 'Extreme nutrients. Dense cyanobacterial blooms. Bad WFD status. Immediate action required.' },
  { status: 'unknown',       label: 'Unknown',        tpRange: '—',       tnRange: '—',       chlaRange: '—',     color: '#64748b', risk: 'Status not assessed. Baseline monitoring required.' },
];

// ─── Water body types ──────────────────────────────────────────────────────────

const WATER_BODY_TYPES: { value: ReservoirConfig['waterBodyType']; label: string; notes: string }[] = [
  { value: 'drinking_water_reservoir', label: 'Drinking Water Reservoir', notes: 'Highest treatment standard. Cyanotoxin monitoring essential. WHO/DWQG apply.' },
  { value: 'irrigation_storage',       label: 'Irrigation Storage',       notes: 'Agriculture water quality. Pathogen limits. Less stringent nutrient targets.' },
  { value: 'recreational_lake',        label: 'Recreational Lake',        notes: 'Contact recreation — Chl-a < 25 µg/L, cyanobacteria alert levels apply.' },
  { value: 'ecological_reserve',       label: 'Ecological Reserve',       notes: 'Environmental flow and water quality targets. WFD "Good Ecological Status" required.' },
  { value: 'industrial_storage',       label: 'Industrial Storage',       notes: 'Process water. Quality targets driven by industrial requirements.' },
  { value: 'stormwater_detention',     label: 'Stormwater Detention',     notes: 'Flood attenuation and NPS pollution control. Sediment and nutrient settling.' },
];

// ─── Compliance limits by water body type ────────────────────────────────────

const COMPLIANCE_LIMITS: Record<string, { tp: number; tn: number; chla: number; do_min: number; turbidity: number }> = {
  drinking_water_reservoir: { tp: 0.010, tn: 0.35, chla: 2.5,  do_min: 7.0, turbidity: 5   },
  irrigation_storage:       { tp: 0.050, tn: 1.00, chla: 25,   do_min: 4.0, turbidity: 50  },
  recreational_lake:        { tp: 0.025, tn: 0.65, chla: 10,   do_min: 5.0, turbidity: 10  },
  ecological_reserve:       { tp: 0.020, tn: 0.50, chla: 8,    do_min: 6.0, turbidity: 10  },
  industrial_storage:       { tp: 0.100, tn: 2.00, chla: 50,   do_min: 2.0, turbidity: 100 },
  stormwater_detention:     { tp: 0.080, tn: 1.50, chla: 25,   do_min: 3.0, turbidity: 50  },
};

// ─── Default reservoir config ─────────────────────────────────────────────────

function defaultConfig(siteId: string, customerId: string): ReservoirConfig {
  return {
    id: siteId, siteId, customerId,
    volumeMl: 10, surfaceAreaHa: 5, maxDepthM: 8, avgDepthM: 4,
    catchmentAreaKm2: 50, annualInflowMl: 30, retentionTimeDays: 122,
    waterBodyType: 'recreational_lake', trophicStatus: 'unknown',
    stratification: 'seasonal',
    targetTpMgL: 0.025, targetTnMgL: 0.65, targetChlaUgL: 10,
    targetNtuTurbidity: 10, targetDoMgL: 5.0,
    aerationSystems: [],
    updatedAt: new Date().toISOString(),
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fv = (v: number, d = 1) => (isFinite(v) && !isNaN(v) ? v.toFixed(d) : '—');
const pct = (v: number) => `${(v * 100).toFixed(0)}%`;

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionCard({ title, icon: Icon, iconColor, children }: {
  title: string; icon: React.ElementType; iconColor?: string; children: React.ReactNode;
}) {
  return (
    <div className="rb-section overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-800/50">
        <Icon className={`w-4 h-4 ${iconColor ?? 'text-slate-400'}`} />
        <h2 className="text-sm font-bold text-slate-200">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function NumInput({ label, value, unit, onChange, min = 0, max = 999999, step = 1, tip }: {
  label: string; value: number; unit?: string; onChange: (v: number) => void;
  min?: number; max?: number; step?: number; tip?: string;
}) {
  return (
    <div className="space-y-0.5">
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          type="number" value={value} min={min} max={max} step={step}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="flex-1 bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200
                     focus:outline-none focus:border-sky-500 text-right font-mono"
        />
        {unit && <span className="text-xs text-slate-500 shrink-0 w-16">{unit}</span>}
      </div>
      {tip && <p className="text-[10px] text-slate-600 leading-relaxed">{tip}</p>}
    </div>
  );
}

// ─── Aeration Management Panel ────────────────────────────────────────────────

function AerationPanel({ config, onChange }: {
  config: ReservoirConfig;
  onChange: (updated: ReservoirConfig) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [newAer, setNewAer] = useState<Partial<AerationSystem>>({
    type: 'surface_aerator', name: '', powerKw: 5, count: 1, saeKgO2PerKwh: 1.8, enabled: true,
  });

  const totalPowerKw = config.aerationSystems.reduce((a, s) => a + s.powerKw * s.count, 0);
  const totalO2KgH   = config.aerationSystems.reduce((a, s) => a + s.powerKw * s.saeKgO2PerKwh * s.count, 0);
  const powerDensityWM3 = config.volumeMl > 0 ? (totalPowerKw * 1000) / (config.volumeMl * 1e6) : 0;

  function addSystem() {
    if (!newAer.name?.trim()) { toast.error('Aeration system name required'); return; }
    const sys: AerationSystem = {
      id: String(Date.now()),
      type: newAer.type ?? 'surface_aerator',
      name: newAer.name!.trim(),
      powerKw: newAer.powerKw ?? 5,
      count: newAer.count ?? 1,
      saeKgO2PerKwh: newAer.saeKgO2PerKwh ?? 1.8,
      depthM: newAer.depthM,
      notes: newAer.notes,
      enabled: true,
    };
    onChange({ ...config, aerationSystems: [...config.aerationSystems, sys] });
    setNewAer({ type: 'surface_aerator', name: '', powerKw: 5, count: 1, saeKgO2PerKwh: 1.8, enabled: true });
    setAdding(false);
    toast.success(`Aeration system "${sys.name}" added`);
  }

  function removeSystem(id: string) {
    onChange({ ...config, aerationSystems: config.aerationSystems.filter(s => s.id !== id) });
  }

  function toggleSystem(id: string) {
    onChange({
      ...config,
      aerationSystems: config.aerationSystems.map(s =>
        s.id === id ? { ...s, enabled: !s.enabled } : s,
      ),
    });
  }

  const selectedType = AERATION_TYPES.find(t => t.value === newAer.type);

  return (
    <div className="space-y-4">
      {/* KPI bar */}
      {config.aerationSystems.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Installed Power', value: fv(totalPowerKw, 1), unit: 'kW', color: '#06b6d4' },
            { label: 'O₂ Transfer Rate', value: fv(totalO2KgH, 1), unit: 'kg O₂/h', color: '#10b981' },
            { label: 'Power Density', value: (powerDensityWM3 * 1000).toFixed(3), unit: 'mW/m³', color: '#f59e0b' },
          ].map(k => (
            <div key={k.label} className="p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-center">
              <div className="text-[10px] text-slate-500 mb-1">{k.label}</div>
              <div className="text-lg font-bold font-mono" style={{ color: k.color }}>{k.value}</div>
              <div className="text-[10px] text-slate-600">{k.unit}</div>
            </div>
          ))}
        </div>
      )}

      {/* Existing systems */}
      {config.aerationSystems.map(sys => {
        const o2Rate = sys.powerKw * sys.saeKgO2PerKwh * sys.count;
        return (
          <div key={sys.id} className={`p-4 rounded-2xl border ${sys.enabled ? 'border-cyan-700/40 bg-cyan-500/5' : 'border-slate-800/40 bg-rb-elevated opacity-60'}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => toggleSystem(sys.id)}
                className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 transition-colors ${sys.enabled ? 'bg-cyan-500 border-cyan-500' : 'bg-transparent border-slate-600'}`}>
                {sys.enabled && <CheckCircle2 className="w-full h-full text-white p-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm text-slate-200">{sys.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-800/40">
                    {AERATION_TYPES.find(t => t.value === sys.type)?.label ?? sys.type}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-slate-400">
                  <span><strong className="text-slate-200">{sys.count} ×</strong> {sys.powerKw} kW</span>
                  <span>SAE <strong className="text-cyan-400">{sys.saeKgO2PerKwh}</strong> kg O₂/kWh</span>
                  <span>O₂ rate <strong className="text-emerald-400">{fv(o2Rate, 1)}</strong> kg/h</span>
                  {sys.depthM && <span>Depth <strong className="text-slate-300">{sys.depthM}</strong> m</span>}
                </div>
                {sys.notes && <p className="text-[10px] text-slate-600 mt-1">{sys.notes}</p>}
              </div>
              <button onClick={() => removeSystem(sys.id)} className="text-slate-600 hover:text-rose-400 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}

      {/* Add form */}
      {adding ? (
        <div className="p-4 rounded-2xl border border-cyan-700/40 bg-cyan-500/5 space-y-3">
          <h4 className="text-xs font-bold text-cyan-300 uppercase tracking-wider">New Aeration System</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">System Name *</label>
              <input value={newAer.name ?? ''} onChange={e => setNewAer(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. North Bay Surface Aerator"
                className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-cyan-500" />
            </div>
            <div className="col-span-2 space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Type</label>
              <select value={newAer.type} onChange={e => {
                const t = AERATION_TYPES.find(x => x.value === e.target.value);
                setNewAer(p => ({ ...p, type: e.target.value as AerationSystem['type'], saeKgO2PerKwh: t?.sae ?? 1.8 }));
              }}
                className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-sm text-slate-200 focus:outline-none focus:border-cyan-500">
                {AERATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {selectedType && <p className="text-[10px] text-slate-600">{selectedType.notes}</p>}
            </div>
            <NumInput label="Power per unit (kW)" value={newAer.powerKw ?? 5} onChange={v => setNewAer(p => ({ ...p, powerKw: v }))} min={0.1} max={1000} step={0.5} />
            <NumInput label="Number of units" value={newAer.count ?? 1} onChange={v => setNewAer(p => ({ ...p, count: Math.max(1, Math.round(v)) }))} min={1} max={100} />
            <NumInput label="SAE (kg O₂/kWh)" value={newAer.saeKgO2PerKwh ?? 1.8} onChange={v => setNewAer(p => ({ ...p, saeKgO2PerKwh: v }))} min={0.1} max={10} step={0.1} />
            <NumInput label="Depth (m, optional)" value={newAer.depthM ?? 0} onChange={v => setNewAer(p => ({ ...p, depthM: v || undefined }))} min={0} max={50} step={0.5} />
          </div>
          <div className="flex gap-2">
            <button onClick={addSystem} className="px-4 py-2 text-xs font-bold text-black bg-cyan-400 hover:bg-cyan-300 rounded-xl transition-colors flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add System
            </button>
            <button onClick={() => setAdding(false)} className="px-4 py-2 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-xl transition-colors">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)}
          className="w-full py-3 rounded-2xl border-2 border-dashed border-cyan-800/50 text-cyan-600 hover:text-cyan-400 hover:border-cyan-700 transition-colors text-sm font-bold flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" /> Add Aeration System
        </button>
      )}

      {config.aerationSystems.length === 0 && !adding && (
        <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-200/70">
            No aeration systems configured. Aeration is standard treatment for reservoirs and lakes — it maintains DO levels, prevents stratification, controls algal growth and reduces P release from sediments.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Process Train Builder ─────────────────────────────────────────────────────

function ProcessTrainPanel({ siteId, customerId }: { siteId: string; customerId: string }) {
  const [processes, setProcesses] = useState<SiteProcess[]>(() => getLocalSiteProcesses(siteId));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<SiteProcess['trainType']>('treatment');
  const [openCategory, setOpenCategory] = useState<string | null>('Aeration');

  function createProcess() {
    if (!newName.trim()) { toast.error('Process name required'); return; }
    const p = saveSiteProcess({ siteId, customerId, name: newName.trim(), trainType: newType, equipment: [] });
    setProcesses(prev => [p, ...prev]);
    setCreating(false);
    setNewName('');
    setEditingId(p.id);
    toast.success(`Process "${p.name}" created`);
  }

  function deleteProcess(id: string) {
    if (!confirm('Delete this process? Cannot be undone.')) return;
    deleteSiteProcess(id);
    setProcesses(prev => prev.filter(p => p.id !== id));
    if (editingId === id) setEditingId(null);
  }

  function addEquipment(processId: string, equip: EquipDef) {
    const proc = processes.find(p => p.id === processId);
    if (!proc) return;
    const item: SiteProcessItem = {
      id: equip.id,
      name: equip.name,
      category: equip.category,
      order: proc.equipment.length,
      enabled: true,
    };
    const updated = { ...proc, equipment: [...proc.equipment, item] };
    updateSiteProcess(processId, { equipment: updated.equipment });
    setProcesses(prev => prev.map(p => p.id === processId ? updated : p));
  }

  function removeEquipment(processId: string, equipId: string, order: number) {
    const proc = processes.find(p => p.id === processId);
    if (!proc) return;
    const equipment = proc.equipment
      .filter(e => !(e.id === equipId && e.order === order))
      .map((e, i) => ({ ...e, order: i }));
    updateSiteProcess(processId, { equipment });
    setProcesses(prev => prev.map(p => p.id === processId ? { ...p, equipment } : p));
  }

  const editingProcess = processes.find(p => p.id === editingId);

  const categorisedEquip = CATEGORY_ORDER.map(cat => ({
    cat,
    color: CATEGORY_COLORS[cat] ?? '#64748b',
    items: RESERVOIR_EQUIPMENT.filter(e => e.category === cat),
  }));

  return (
    <div className="space-y-4">
      {/* Process list */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">{processes.length} process{processes.length !== 1 ? 'es' : ''} configured</span>
        <button onClick={() => setCreating(true)}
          className="px-3 py-1.5 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-xl transition-colors flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> New Process
        </button>
      </div>

      {creating && (
        <div className="p-4 rounded-2xl border border-amber-700/40 bg-amber-500/5 space-y-3">
          <h4 className="text-xs font-bold text-amber-300 uppercase tracking-wider">New Process</h4>
          <input value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="e.g. Inlet Wetland Treatment"
            className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-amber-500" />
          <select value={newType} onChange={e => setNewType(e.target.value as SiteProcess['trainType'])}
            className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-sm text-slate-200 focus:outline-none focus:border-amber-500">
            <option value="treatment">Treatment Train</option>
            <option value="aeration">Aeration System</option>
            <option value="dosing">Chemical Dosing</option>
            <option value="monitoring">Monitoring</option>
            <option value="other">Other</option>
          </select>
          <div className="flex gap-2">
            <button onClick={createProcess} className="px-4 py-2 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-xl transition-colors">Create</button>
            <button onClick={() => setCreating(false)} className="px-4 py-2 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 rounded-xl transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {processes.length === 0 && !creating && (
        <div className="p-8 text-center text-slate-600 rb-card">
          <Layers className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No processes yet — create your first process train</p>
        </div>
      )}

      {/* Process cards */}
      {processes.map(proc => {
        const isEditing = proc.id === editingId;
        return (
          <div key={proc.id} className="rb-section overflow-hidden">
            <button onClick={() => setEditingId(isEditing ? null : proc.id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-rb-elevated transition-colors">
              <div className="flex items-center gap-2">
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${
                  proc.trainType === 'aeration'    ? 'bg-cyan-500/10 text-cyan-400 border-cyan-800/40' :
                  proc.trainType === 'dosing'      ? 'bg-orange-500/10 text-orange-400 border-orange-800/40' :
                  proc.trainType === 'monitoring'  ? 'bg-slate-800 text-slate-400 border-slate-700/40' :
                                                     'bg-amber-500/10 text-amber-400 border-amber-800/40'
                }`}>{proc.trainType.toUpperCase()}</span>
                <span className="text-sm font-bold text-slate-200">{proc.name}</span>
                <span className="text-[11px] text-slate-500">{proc.equipment.length} equipment</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={e => { e.stopPropagation(); deleteProcess(proc.id); }}
                  className="p-1 text-slate-600 hover:text-rose-400 transition-colors rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                {isEditing ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
              </div>
            </button>

            {isEditing && (
              <div className="border-t border-slate-800/50">
                {/* Current equipment in train */}
                {proc.equipment.length > 0 && (
                  <div className="px-4 py-3 space-y-2 border-b border-slate-800/40 bg-rb-elevated/50">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Process Train Sequence</div>
                    <div className="flex flex-wrap gap-2 items-center">
                      {proc.equipment.map((item, idx) => {
                        const color = CATEGORY_COLORS[item.category] ?? '#64748b';
                        return (
                          <div key={`${item.id}-${item.order}`} className="flex items-center gap-1.5">
                            {idx > 0 && <span className="text-slate-700 text-lg">→</span>}
                            <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl border text-xs font-bold"
                              style={{ borderColor: `${color}40`, background: `${color}10`, color }}>
                              {item.name}
                              <button onClick={() => removeEquipment(proc.id, item.id, item.order)}
                                className="ml-1 text-slate-600 hover:text-rose-400 transition-colors">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Equipment browser */}
                <div className="px-4 py-4 space-y-2">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Add Equipment</div>
                  {categorisedEquip.map(({ cat, color, items }) => (
                    <div key={cat}>
                      <button onClick={() => setOpenCategory(openCategory === cat ? null : cat)}
                        className="w-full flex items-center justify-between py-1.5 text-xs font-bold hover:text-slate-200 transition-colors"
                        style={{ color }}>
                        <span>{cat} ({items.length})</span>
                        {openCategory === cat ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      {openCategory === cat && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-2">
                          {items.map(eq => (
                            <button key={eq.id} onClick={() => addEquipment(proc.id, eq)}
                              className="text-left px-3 py-2 rounded-xl border border-slate-800/60 bg-rb-elevated hover:border-slate-600 transition-all group">
                              <div className="text-[11px] font-bold text-slate-300 group-hover:text-slate-100">{eq.name}</div>
                              <div className="text-[10px] text-slate-600 mt-0.5 line-clamp-1">{eq.purpose}</div>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {eq.tpRemoval && <span className="text-[9px] text-emerald-500">TP −{pct(eq.tpRemoval)}</span>}
                                {eq.tnRemoval && <span className="text-[9px] text-sky-500">TN −{pct(eq.tnRemoval)}</span>}
                                {eq.tssRemoval && <span className="text-[9px] text-amber-500">TSS −{pct(eq.tssRemoval)}</span>}
                                {eq.doGain && <span className="text-[9px] text-cyan-400">DO +{eq.doGain} mg/L·h</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Compliance Monitor ───────────────────────────────────────────────────────

function CompliancePanel({ config, influentTp, influentTn }: {
  config: ReservoirConfig;
  influentTp: number;
  influentTn: number;
}) {
  const limits = COMPLIANCE_LIMITS[config.waterBodyType];
  const trophic = TROPHIC.find(t => t.status === config.trophicStatus) ?? TROPHIC[4];

  function flag(current: number, target: number) {
    const ratio = target > 0 ? current / target : 0;
    if (ratio <= 1)   return { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-800/40', icon: CheckCircle2, label: 'Compliant' };
    if (ratio <= 1.5) return { color: 'text-amber-400',   bg: 'bg-amber-500/10  border-amber-800/40',   icon: AlertTriangle,  label: 'Near limit' };
    return              { color: 'text-rose-400',    bg: 'bg-rose-500/10   border-rose-800/40',    icon: AlertTriangle,  label: 'Exceedance' };
  }

  const tpFlag = flag(influentTp, limits.tp);
  const tnFlag = flag(influentTn, limits.tn);

  return (
    <div className="space-y-4">
      {/* Trophic state */}
      <div className="p-4 rounded-2xl border-2" style={{ borderColor: `${trophic.color}40`, background: `${trophic.color}08` }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full" style={{ background: trophic.color }} />
          <span className="text-sm font-bold" style={{ color: trophic.color }}>{trophic.label} State</span>
        </div>
        <p className="text-[11px] text-slate-400 mb-3">{trophic.risk}</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'TP range',   val: trophic.tpRange,   unit: 'mg/L' },
            { label: 'TN range',   val: trophic.tnRange,   unit: 'mg/L' },
            { label: 'Chl-a range',val: trophic.chlaRange, unit: 'µg/L' },
          ].map(r => (
            <div key={r.label} className="text-center p-2 rounded-xl bg-rb-elevated border border-slate-800/40">
              <div className="text-[9px] text-slate-600 uppercase">{r.label}</div>
              <div className="text-xs font-mono font-bold text-slate-300">{r.val}</div>
              <div className="text-[9px] text-slate-700">{r.unit}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance limits vs targets */}
      <div className="space-y-2">
        <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Regulatory Limits — {WATER_BODY_TYPES.find(w => w.value === config.waterBodyType)?.label}</h4>
        {[
          { param: 'Total Phosphorus', current: influentTp, target: limits.tp,  configTarget: config.targetTpMgL, unit: 'mg/L', Flag: tpFlag },
          { param: 'Total Nitrogen',   current: influentTn, target: limits.tn,  configTarget: config.targetTnMgL, unit: 'mg/L', Flag: tnFlag },
          { param: 'Target DO',        current: 0,          target: limits.do_min, configTarget: config.targetDoMgL, unit: 'mg/L', Flag: { color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-800/40', icon: Info, label: 'Target' } },
          { param: 'Chlorophyll-a',    current: 0,          target: limits.chla, configTarget: config.targetChlaUgL, unit: 'µg/L', Flag: { color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-800/40', icon: Info, label: 'Monitor' } },
          { param: 'Turbidity',        current: 0,          target: limits.turbidity, configTarget: config.targetNtuTurbidity, unit: 'NTU', Flag: { color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-800/40', icon: Info, label: 'Monitor' } },
        ].map(row => {
          const { Flag } = row;
          const FlagIcon = Flag.icon;
          return (
            <div key={row.param} className="flex items-center justify-between px-3 py-2.5 rounded-xl border bg-rb-elevated border-slate-800/50">
              <div className="min-w-0">
                <div className="text-xs font-bold text-slate-300">{row.param}</div>
                {row.current > 0 && (
                  <div className="text-[10px] text-slate-500">
                    Influent: <span className={`font-mono ${Flag.color}`}>{fv(row.current, 3)}</span> {row.unit}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0 ml-4">
                <div className="text-xs font-mono text-slate-400">Limit: <strong className="text-slate-200">{fv(row.target, 3)}</strong> {row.unit}</div>
                <div className={`flex items-center gap-1 justify-end mt-0.5 text-[10px] font-bold ${Flag.color}`}>
                  <FlagIcon className="w-3 h-3" /> {Flag.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key risks */}
      <div className="space-y-2">
        <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Key Risk Indicators</h4>
        {[
          {
            show: influentTp > limits.tp,
            color: 'amber',
            msg: `TP ${fv(influentTp, 3)} mg/L exceeds limit ${fv(limits.tp, 3)} mg/L — eutrophication and algal bloom risk. Consider alum dosing or Phoslock application.`,
          },
          {
            show: config.trophicStatus === 'eutrophic' || config.trophicStatus === 'hypertrophic',
            color: 'rose',
            msg: `${trophic.label} status — immediate nutrient reduction, aeration and potential dredging required. Cyanobacteria bloom and cyanotoxin risk.`,
          },
          {
            show: config.stratification !== 'well_mixed' && config.aerationSystems.filter(a => a.enabled && (a.type === 'destratifier' || a.type === 'draft_tube_mixer')).length === 0,
            color: 'amber',
            msg: 'Stratified water body with no destratification aeration — anoxic hypolimnion and internal phosphorus loading from sediments is likely.',
          },
          {
            show: config.aerationSystems.filter(a => a.enabled).length === 0,
            color: 'slate',
            msg: 'No aeration systems active — dissolved oxygen management is passive. Consider aeration for water quality improvement.',
          },
        ].filter(r => r.show).map((r, i) => (
          <div key={i} className={`flex items-start gap-2.5 rounded-xl px-3.5 py-3 bg-${r.color}-500/5 border border-${r.color}-500/20`}>
            <AlertTriangle className={`w-4 h-4 text-${r.color}-400 shrink-0 mt-0.5`} />
            <p className={`text-[11px] text-${r.color}-200/80`}>{r.msg}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Compliance Matrix Data ───────────────────────────────────────────────────

interface ComplianceTemplateItem {
  ref: string;
  categoryCode: string;  // W, R, E, H, D, C, …
  category: string;
  requirement: string;
  standard: string;
  evidenceRequired: string;
  obligationLevel: ObligationLevel;
}

// ── UK / GB compliance matrix (full matrix from DWI / EA / HSE / Reservoirs Act) ──
const GB_COMPLIANCE: ComplianceTemplateItem[] = [
  // Water Quality
  { ref:'W1', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'All materials in contact with raw water approved for potable use', standard:'DWI Regulation 31 (Water Supply (Water Quality) Regulations 2016)', evidenceRequired:'DWI listed number for EPDM membrane compound, 316L stainless steel, HDPE pipe, adhesives, coatings' },
  { ref:'W2', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'Non-metallic materials do not impart taste, odour, or toxicity', standard:'BS 6920 (Effect on water quality)', evidenceRequired:'Current BS 6920 test certificates for EPDM membrane and any non-metallic components' },
  { ref:'W3', categoryCode:'W', category:'Water Quality',        obligationLevel:'Required',  requirement:'Product-level approval for water fittings', standard:'WRAS Approval (or equivalent KIWA/NSF)', evidenceRequired:'WRAS certificate for diffuser membrane and flexible hose assemblies' },
  { ref:'W4', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'System must not deteriorate final water quality', standard:'Water Supply (Water Quality) Regulations 2016; Drinking Water Safety Plan (DWSP)', evidenceRequired:"Risk assessment within operator's DWSP; notification to DWI of treatment modification" },
  // Reservoir Safety
  { ref:'R1', categoryCode:'R', category:'Reservoir Safety',     obligationLevel:'Mandatory', requirement:'Works must not compromise reservoir safety', standard:'Reservoirs Act 1975 (as amended); All Reservoirs (Safety Reports) Regulations 2015', evidenceRequired:'Liaison with Supervising Engineer; confirmation that bed works do not affect embankment drainage or outlet structures' },
  { ref:'R2', categoryCode:'R', category:'Reservoir Safety',     obligationLevel:'Required',  requirement:'Structural integrity of any in-reservoir frames', standard:'BS EN 1090-1 & -2 (Execution of steel structures); BS EN 1993 (Eurocode 3)', evidenceRequired:'Factory Production Control (FPC) documentation; welding procedure specification (WPS) for 316L frames' },
  // Environmental
  { ref:'E1', categoryCode:'E', category:'Environmental',        obligationLevel:'Mandatory', requirement:'Works within abstraction licence conditions', standard:'Environment Agency Abstraction Licence', evidenceRequired:'Confirmation from operator that drawdown/installation window is within EA licence; no breach of impoundment conditions' },
  { ref:'E2', categoryCode:'E', category:'Environmental',        obligationLevel:'Mandatory', requirement:'No pollution of controlled waters during installation', standard:'Environmental Permitting Regulations 2016; Water Resources Act 1991', evidenceRequired:'Spill prevention plan; bunding for fuels/hydraulic fluids; working within dredging contractor discharge permit' },
  { ref:'E3', categoryCode:'E', category:'Environmental',        obligationLevel:'Required',  requirement:'Protection of habitats and species', standard:'Wildlife and Countryside Act 1981; NERC Act 2006', evidenceRequired:'Ecological survey / PEA; installation programme avoiding fish spawning (Mar–Jun) and bird nesting (Feb–Aug)' },
  { ref:'E4', categoryCode:'E', category:'Environmental',        obligationLevel:'Advisory',  requirement:'No deterioration of WFD ecological status', standard:'Water Framework Directive (UK transposition)', evidenceRequired:'Operator to confirm project screened under WFD; aeration design to avoid excessive mixing that stimulates algal blooms' },
  // Health & Safety
  { ref:'H1', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Designer duties for health & safety', standard:'CDM 2015 (Construction (Design and Management) Regulations)', evidenceRequired:'Designer risk assessment (drowning, diving, manual handling, confined space); pre-construction information pack' },
  { ref:'H2', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Safe diving operations', standard:'Diving at Work Regulations 1997; HSE ACOP L104', evidenceRequired:'Diving contractor with HSE-approved code of practice; dive plan, emergency rescue cover, medical fitness records' },
  { ref:'H3', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Safe lifting and plant operations', standard:'LOLER 1998; PUWER 1998; Work at Height Regulations 2005', evidenceRequired:'Thorough examination certificates for lifting equipment; safe systems for pontoon/embankment work' },
  // Design & Technical
  { ref:'D1', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Required',  requirement:'Fine-bubble diffuser performance', standard:'ASCE 2-06 or DWI clean-water test protocol', evidenceRequired:'Manufacturer\'s SOTE test data at 20 °C; proof of ≤ 3 mm Sauter mean bubble diameter at design flow' },
  { ref:'D2', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Mandatory', requirement:'Electrical safety and energy efficiency', standard:'BS 7671 (IET Wiring Regulations); IEC 60034 (rotating machines)', evidenceRequired:'Electrical design certificate; blower motor IE3/IE4 efficiency rating; IP rating for wet environments' },
  { ref:'D3', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Required',  requirement:'Plastic piping for submerged air main', standard:'BS EN 12201 / BS EN 1555', evidenceRequired:'Pressure rating and UV-stabilisation certificates for HDPE air main and flexible hoses' },
  // Commercial & Contractual
  { ref:'C1', categoryCode:'C', category:'Commercial',           obligationLevel:'Required',  requirement:'Contractual framework', standard:'NEC3/NEC4 ECC or TSC (as per operator capital delivery)', evidenceRequired:'Confirmation of contract option (A, C, or ECI); understanding of risk allocation and design liability' },
  { ref:'C2', categoryCode:'C', category:'Commercial',           obligationLevel:'Required',  requirement:'Asset management and whole-life data', standard:'ISO 55000 / PAS 55; operator CMMS (e.g., Maximo)', evidenceRequired:'Asset register data (tag numbers, materials, design life); maintenance schedule and spares list' },
  { ref:'C3', categoryCode:'C', category:'Commercial',           obligationLevel:'Mandatory', requirement:'Reservoir Act liaison', standard:'Reservoirs Act 1975; Panel Engineer engagement', evidenceRequired:'Written confirmation from operator that Supervising Engineer has reviewed and approved bed-works scope' },
];

// ── Australia compliance matrix ──
const AU_COMPLIANCE: ComplianceTemplateItem[] = [
  { ref:'W1', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'Drinking water quality meets ADWG parameters', standard:'Australian Drinking Water Guidelines (ADWG 2011 v3.8)', evidenceRequired:'ADWG compliance monitoring plan; laboratory results for microbiological, chemical and physical parameters' },
  { ref:'W2', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'Materials in contact with potable water are WaterMark certified', standard:'WaterMark Certification Scheme (AS/NZS 4020)', evidenceRequired:'WaterMark licence numbers for all submerged components; AS/NZS 4020 test certificates' },
  { ref:'W3', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'Cyanobacteria and algal toxin management plan', standard:'ADWG 2011 — Cyanobacteria chapter; state EPA guidance', evidenceRequired:'Blue-green algae monitoring protocol; chlorophyll-a action thresholds; cyanotoxin testing trigger plan' },
  { ref:'W4', categoryCode:'W', category:'Water Quality',        obligationLevel:'Required',  requirement:'System modifications notified to state health authority', standard:'Public Health Acts (state); Water Quality Regulations', evidenceRequired:'Notification letter to relevant state agency; updated drinking water quality management plan (DWQMP)' },
  // Dam Safety
  { ref:'R1', categoryCode:'R', category:'Dam / Reservoir Safety', obligationLevel:'Mandatory', requirement:'Works comply with state Dam Safety Act', standard:'Dam Safety Act 2015 (NSW) / Water Act 2000 (QLD) / equivalent state legislation', evidenceRequired:'Supervising Engineer approval; confirmation works do not affect dam safety-critical elements' },
  { ref:'R2', categoryCode:'R', category:'Dam / Reservoir Safety', obligationLevel:'Required',  requirement:'Structural work by registered structural engineer', standard:'AS 4100 (Steel structures); AS 3600 (Concrete)', evidenceRequired:'Engineer design certificate; inspection hold points; ITP for in-lake steelwork' },
  // Environmental
  { ref:'E1', categoryCode:'E', category:'Environmental',        obligationLevel:'Mandatory', requirement:'Works comply with Water Act / Resource licence conditions', standard:'State Water Act; EA Licence / Licence to Take Water', evidenceRequired:'Licence conditions review; confirmation works are within approved purposes and volumes' },
  { ref:'E2', categoryCode:'E', category:'Environmental',        obligationLevel:'Mandatory', requirement:'No pollution of waters of the state', standard:'Protection of the Environment Operations Act 1997 (NSW) / equivalent; National Environment Protection Measures', evidenceRequired:'Environmental management plan; spill prevention and response procedure; water quality monitoring during works' },
  { ref:'E3', categoryCode:'E', category:'Environmental',        obligationLevel:'Required',  requirement:'Protection of threatened species and ecological communities', standard:'EPBC Act 1999 (Federal); state threatened species legislation', evidenceRequired:'Flora and fauna assessment; avoid spawning and migratory bird periods; EPBC referral if applicable' },
  { ref:'E4', categoryCode:'E', category:'Environmental',        obligationLevel:'Advisory',  requirement:'Maintain ecological values of the water body', standard:'ANZECC 2000 Water Quality Guidelines; State Environmental Values Policy', evidenceRequired:'Baseline ecological assessment; post-works monitoring of aquatic macroinvertebrates and fish' },
  // Health & Safety
  { ref:'H1', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Principal contractor and designer WHS duties', standard:'Model WHS Laws (Safe Work Australia); state WHS Acts', evidenceRequired:'Safe Work Method Statement (SWMS); construction induction; site safety plan' },
  { ref:'H2', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Safe diving operations in accordance with regulations', standard:'AS/NZS 2299.1 (Occupational diving); Safe Work Australia Code of Practice — Diving', evidenceRequired:'Registered diving supervisor; dive safety plan; medical fitness; emergency procedures' },
  { ref:'H3', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Plant and equipment compliance', standard:'WHS Regulations; AS 1418 (Cranes and hoists series)', evidenceRequired:'Plant registration certificates; pre-start inspection checklists; operator licences' },
  // Design & Technical
  { ref:'D1', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Required',  requirement:'Aeration system performance documented', standard:'AS 2703 (Diffused aeration) or manufacturer SOTE test data', evidenceRequired:'SOTE test results at clean water conditions; field verification protocol' },
  { ref:'D2', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Mandatory', requirement:'Electrical installation to AS/NZS 3000', standard:'AS/NZS 3000 (Wiring Rules); AS/NZS 60034 (Rotating machines)', evidenceRequired:'Certificate of compliance for electrical installation; IP rating certificates for submerged/outdoor equipment' },
  // Commercial
  { ref:'C1', categoryCode:'C', category:'Commercial',           obligationLevel:'Required',  requirement:'Contractual framework with operator', standard:'AS 4000 / AS 4905 (Construction contracts); state government contract templates', evidenceRequired:'Executed contract; design liability schedule; defects liability period agreed' },
  { ref:'C2', categoryCode:'C', category:'Commercial',           obligationLevel:'Required',  requirement:'Asset data handover', standard:'ISO 55000; operator asset management system', evidenceRequired:'As-built drawings; O&M manual; spare parts list; asset register entries' },
];

// ── USA compliance matrix ──
const US_COMPLIANCE: ComplianceTemplateItem[] = [
  { ref:'W1', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'All materials NSF/ANSI 61 certified for drinking water contact', standard:'NSF/ANSI 61 (Drinking Water System Components)', evidenceRequired:'NSF/ANSI 61 certification documents for all submerged components, membranes and coatings' },
  { ref:'W2', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'SDWA compliance — no impairment of public water supply', standard:'Safe Drinking Water Act (SDWA); 40 CFR Parts 141–143', evidenceRequired:'State primacy agency notification; risk assessment; SWMPP/DWSP update' },
  { ref:'W3', categoryCode:'W', category:'Water Quality',        obligationLevel:'Required',  requirement:'Cyanotoxin monitoring for drinking water reservoirs', standard:'EPA Health Advisory (2015); State Implementation Guidance', evidenceRequired:'Cyanotoxin monitoring plan; action thresholds per EPA advisory levels (1 µg/L MCYSTs for adults)' },
  // Dam Safety
  { ref:'R1', categoryCode:'R', category:'Dam Safety',           obligationLevel:'Mandatory', requirement:'Works comply with state dam safety program', standard:'State Dam Safety Acts; FEMA P-93 (Emergency Action Planning)', evidenceRequired:'State dam safety engineer review and sign-off; confirmation works do not affect spillway, embankment, or outlet' },
  { ref:'R2', categoryCode:'R', category:'Dam Safety',           obligationLevel:'Required',  requirement:'Structural design to appropriate codes', standard:'ASCE 7-22 (Minimum Design Loads); AISC 360 (Steel); ACI 318 (Concrete)', evidenceRequired:'Licensed PE-stamped drawings; inspection checklists; materials certifications' },
  // Environmental
  { ref:'E1', categoryCode:'E', category:'Environmental',        obligationLevel:'Mandatory', requirement:'Section 404 / Section 10 permits if impacting waters of the US', standard:'Clean Water Act Section 404; Rivers and Harbors Act Section 10; USACE nationwide permits', evidenceRequired:'Nationwide permit verification or individual permit; USACE pre-construction notification if required' },
  { ref:'E2', categoryCode:'E', category:'Environmental',        obligationLevel:'Mandatory', requirement:'No discharge of pollutants without permit', standard:'CWA Section 402 NPDES; 40 CFR Parts 122–125', evidenceRequired:'NPDES construction stormwater permit (CGP); SWPPP; turbidity and pH monitoring' },
  { ref:'E3', categoryCode:'E', category:'Environmental',        obligationLevel:'Required',  requirement:'Endangered Species Act Section 7 consultation', standard:'Endangered Species Act (ESA) Section 7; USFWS / NMFS', evidenceRequired:'Biological assessment; informal or formal consultation with FWS; conditions of any Biological Opinion' },
  { ref:'E4', categoryCode:'E', category:'Environmental',        obligationLevel:'Advisory',  requirement:'Consistency with state water quality standards', standard:'CWA Section 303; state water quality standards (designated uses)', evidenceRequired:'State agency confirmation no impairment of designated use; 401 water quality certification if required' },
  // Health & Safety
  { ref:'H1', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'OSHA construction safety compliance', standard:'OSHA 29 CFR 1926 (Construction Safety); OSHA 29 CFR 1910 (General Industry)', evidenceRequired:'Site safety plan; competent person designations; fall protection, excavation, and confined space procedures' },
  { ref:'H2', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Commercial diving operations compliance', standard:'OSHA 29 CFR 1910 Subpart T (Commercial Diving); ACGIH guidelines', evidenceRequired:'Dive plan; tending vessel; diver medical certifications; emergency procedures; OSHA-compliant dive log' },
  { ref:'H3', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Crane and rigging safety', standard:'OSHA 1926.1400 (Cranes); ASME B30.5; ANSI/ASSP A10.22', evidenceRequired:'Operator certifications (CCO); crane inspection records; rigging plan; load calculations' },
  // Design & Technical
  { ref:'D1', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Required',  requirement:'Diffused aeration performance standards', standard:'ASCE 2-06 (Standard for Diffused Air Aeration Systems)', evidenceRequired:'Manufacturer SOTE test data; shop drawings; field acceptance test procedure' },
  { ref:'D2', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Mandatory', requirement:'Electrical compliance — NEC and NEMA standards', standard:'NFPA 70 (National Electrical Code); NEMA 250 (Enclosures)', evidenceRequired:'Licensed electrician sign-off; AHJ inspection approval; NEMA rating certificates for outdoor/submerged equipment' },
  // Commercial
  { ref:'C1', categoryCode:'C', category:'Commercial',           obligationLevel:'Required',  requirement:'Contract compliance with owner procurement rules', standard:'FAR/DFARS (if federal); state procurement codes; owner specifications', evidenceRequired:'Executed contract; bond and insurance certificates; prevailing wage compliance if required' },
  { ref:'C2', categoryCode:'C', category:'Commercial',           obligationLevel:'Required',  requirement:'Asset information and O&M handover', standard:'ISO 55000; AWWA M49 (Condition Assessment); owner asset management system', evidenceRequired:'As-built record drawings; O&M manual; spare parts list; commissioning test results' },
];

// ── Generic / Other countries ──
const GENERIC_COMPLIANCE: ComplianceTemplateItem[] = [
  { ref:'W1', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'Materials suitable for potable/raw water contact', standard:'WHO Guidelines for Drinking-water Quality (4th ed.); national equivalent', evidenceRequired:'Material conformity certificates; independent testing to applicable standard' },
  { ref:'W2', categoryCode:'W', category:'Water Quality',        obligationLevel:'Mandatory', requirement:'No deterioration of drinking water quality', standard:'National drinking water regulations; WHO GDWQ', evidenceRequired:'Risk assessment; notification to water authority; monitoring plan' },
  { ref:'R1', categoryCode:'R', category:'Reservoir Safety',     obligationLevel:'Mandatory', requirement:'Works approved by supervising / inspection engineer', standard:'National dam safety legislation; ICOLD guidelines', evidenceRequired:'Approval from competent engineer; confirmation of no impact to dam safety' },
  { ref:'E1', categoryCode:'E', category:'Environmental',        obligationLevel:'Mandatory', requirement:'Compliance with water abstraction / impoundment licence', standard:'National water law; local water authority licence', evidenceRequired:'Licence conditions review; works within authorised purposes' },
  { ref:'E2', categoryCode:'E', category:'Environmental',        obligationLevel:'Mandatory', requirement:'No pollution of surface or groundwater', standard:'National environment protection legislation; ISO 14001', evidenceRequired:'Environmental management plan; spill prevention measures; monitoring' },
  { ref:'E3', categoryCode:'E', category:'Environmental',        obligationLevel:'Required',  requirement:'Protection of biodiversity and ecological values', standard:'National biodiversity / wildlife legislation; CBD obligations', evidenceRequired:'Ecological assessment; avoidance of sensitive periods; habitat protection measures' },
  { ref:'H1', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Occupational health and safety compliance', standard:'National OHS/WHS legislation; ISO 45001', evidenceRequired:'Site safety plan; risk assessments; competent supervision' },
  { ref:'H2', categoryCode:'H', category:'Health & Safety',      obligationLevel:'Mandatory', requirement:'Safe working in and around water', standard:'National diving/marine regulations; ILO guidelines', evidenceRequired:'Trained and certificated personnel; emergency procedures; rescue equipment' },
  { ref:'D1', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Required',  requirement:'Equipment performance documentation', standard:'ISO 17025 (laboratory testing); manufacturer test data', evidenceRequired:'Performance test reports; commissioning protocol; operational acceptance criteria' },
  { ref:'D2', categoryCode:'D', category:'Design & Technical',   obligationLevel:'Mandatory', requirement:'Electrical safety compliance', standard:'National electrical code; IEC 60364 (Electrical installations)', evidenceRequired:'Electrical design certificate; inspection/approval by competent authority' },
  { ref:'C1', categoryCode:'C', category:'Commercial',           obligationLevel:'Required',  requirement:'Formal contractual arrangement with operator', standard:'National contract law; owner procurement policy', evidenceRequired:'Signed contract; liability provisions; warranty period' },
  { ref:'C2', categoryCode:'C', category:'Commercial',           obligationLevel:'Required',  requirement:'Asset data and O&M handover',        standard:'ISO 55000; owner asset management policy', evidenceRequired:'As-built documentation; O&M manual; spare parts list; training records' },
];

/** Select the right compliance template for the site's country */
function getComplianceTemplate(country?: string): ComplianceTemplateItem[] {
  switch ((country ?? '').toUpperCase()) {
    case 'GB': case 'UK': case 'IE': return GB_COMPLIANCE;
    case 'AU': case 'NZ':            return AU_COMPLIANCE;
    case 'US': case 'CA':            return US_COMPLIANCE;
    default:                          return GENERIC_COMPLIANCE;
  }
}

// ── Category metadata ──
const CATEGORY_META: Record<string, { label: string; color: string }> = {
  W: { label: 'Water Quality',       color: '#06b6d4' },
  R: { label: 'Reservoir Safety',    color: '#f97316' },
  E: { label: 'Environmental',       color: '#10b981' },
  H: { label: 'Health & Safety',     color: '#ef4444' },
  D: { label: 'Design & Technical',  color: '#8b5cf6' },
  C: { label: 'Commercial',          color: '#f59e0b' },
};

const STATUS_META: Record<ComplianceStatus, { label: string; color: string; bg: string; border: string }> = {
  not_started:   { label: 'Not Started',   color: 'text-slate-400',  bg: 'bg-slate-800',       border: 'border-slate-700/40' },
  in_progress:   { label: 'In Progress',   color: 'text-amber-400',  bg: 'bg-amber-500/10',    border: 'border-amber-800/40' },
  compliant:     { label: 'Compliant',     color: 'text-emerald-400',bg: 'bg-emerald-500/10',  border: 'border-emerald-800/40' },
  non_compliant: { label: 'Non-Compliant', color: 'text-rose-400',   bg: 'bg-rose-500/10',     border: 'border-rose-800/40' },
  na:            { label: 'N/A',           color: 'text-slate-600',  bg: 'bg-slate-900',       border: 'border-slate-800/40' },
};

const OBLIGATION_META: Record<ObligationLevel, { color: string; bg: string; border: string }> = {
  Mandatory: { color: 'text-rose-400',   bg: 'bg-rose-500/10',   border: 'border-rose-800/40'   },
  Required:  { color: 'text-amber-400',  bg: 'bg-amber-500/10',  border: 'border-amber-800/40'  },
  Advisory:  { color: 'text-sky-400',    bg: 'bg-sky-500/10',    border: 'border-sky-800/40'    },
};

// ─── Compliance Matrix Panel ──────────────────────────────────────────────────

function defaultOverride(ref: string): ComplianceOverride {
  return {
    ref,
    status: 'not_started',
    evidenceNotes: '',
    responsiblePerson: '',
    targetDate: '',
    completedDate: '',
    updatedAt: new Date().toISOString(),
  };
}

function ComplianceMatrixPanel({ siteId, customerId, country, waterBodyType }: {
  siteId: string;
  customerId: string;
  country?: string;
  waterBodyType: ReservoirConfig['waterBodyType'];
}) {
  const template = useMemo(() => getComplianceTemplate(country), [country]);

  // Load saved compliance data
  const [complianceData, setComplianceData] = useState<SiteComplianceData>(() => {
    const saved = getSiteCompliance(siteId);
    if (saved) return saved;
    // Bootstrap from template with default overrides
    const overrides: Record<string, ComplianceOverride> = {};
    template.forEach(item => { overrides[item.ref] = defaultOverride(item.ref); });
    return { siteId, customerId, overrides, updatedAt: new Date().toISOString() };
  });

  const [filterCategory, setFilterCategory]     = useState<string>('all');
  const [filterObligation, setFilterObligation] = useState<string>('all');
  const [filterStatus, setFilterStatus]         = useState<string>('all');
  const [expandedRef, setExpandedRef]           = useState<string | null>(null);
  const [saving, setSaving]                     = useState(false);
  const [dirty, setDirty]                       = useState(false);
  const [showAddCustom, setShowAddCustom]       = useState(false);
  const [customItem, setCustomItem]             = useState<Partial<ComplianceTemplateItem>>({
    categoryCode: 'W', obligationLevel: 'Required',
  });

  // Merge template with any custom items stored in overrides that don't exist in template
  const allItems = useMemo(() => {
    const templateRefs = new Set(template.map(t => t.ref));
    const extraRefs = Object.keys(complianceData.overrides).filter(r => !templateRefs.has(r));
    // Extra items are stored with a special prefix 'CX' and their data in the override object
    const extras: ComplianceTemplateItem[] = extraRefs
      .map(ref => {
        const ov = complianceData.overrides[ref];
        if (!(ov as any).customRequirement) return null;
        return {
          ref,
          categoryCode: (ov as any).customCategoryCode ?? 'C',
          category: (ov as any).customCategory ?? 'Custom',
          requirement: (ov as any).customRequirement,
          standard: (ov as any).customStandard ?? '',
          evidenceRequired: (ov as any).customEvidenceRequired ?? '',
          obligationLevel: ((ov as any).customObligationLevel ?? 'Required') as ObligationLevel,
        } as ComplianceTemplateItem;
      })
      .filter(Boolean) as ComplianceTemplateItem[];
    return [...template, ...extras];
  }, [template, complianceData.overrides]);

  // Filtered list
  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      const ov = complianceData.overrides[item.ref] ?? defaultOverride(item.ref);
      if (filterCategory !== 'all' && item.categoryCode !== filterCategory) return false;
      if (filterObligation !== 'all' && item.obligationLevel !== filterObligation) return false;
      if (filterStatus !== 'all' && ov.status !== filterStatus) return false;
      return true;
    });
  }, [allItems, complianceData.overrides, filterCategory, filterObligation, filterStatus]);

  // Summary stats
  const stats = useMemo(() => {
    const all = allItems.map(item => complianceData.overrides[item.ref] ?? defaultOverride(item.ref));
    return {
      total: allItems.length,
      compliant:     all.filter(o => o.status === 'compliant').length,
      in_progress:   all.filter(o => o.status === 'in_progress').length,
      not_started:   all.filter(o => o.status === 'not_started').length,
      non_compliant: all.filter(o => o.status === 'non_compliant').length,
      na:            all.filter(o => o.status === 'na').length,
      mandatory_open: allItems.filter(item =>
        item.obligationLevel === 'Mandatory' &&
        ['not_started','non_compliant'].includes(complianceData.overrides[item.ref]?.status ?? 'not_started')
      ).length,
    };
  }, [allItems, complianceData.overrides]);

  function updateOverride(ref: string, updates: Partial<ComplianceOverride>) {
    setComplianceData(prev => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        [ref]: { ...(prev.overrides[ref] ?? defaultOverride(ref)), ...updates, updatedAt: new Date().toISOString() },
      },
    }));
    setDirty(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      saveSiteCompliance(complianceData);
      setDirty(false);
      toast.success('Compliance matrix saved');
    } finally { setSaving(false); }
  }

  function addCustomItem() {
    if (!customItem.requirement?.trim()) { toast.error('Requirement description is required'); return; }
    const ref = `CX${Date.now().toString().slice(-4)}`;
    const catMeta = CATEGORY_META[customItem.categoryCode ?? 'C'];
    updateOverride(ref, {
      status: 'not_started',
      evidenceNotes: '',
      responsiblePerson: '',
      targetDate: '',
      completedDate: '',
      // Store custom item data on override object (prefix CX)
      ...(({
        customCategoryCode: customItem.categoryCode,
        customCategory: catMeta?.label ?? 'Custom',
        customRequirement: customItem.requirement,
        customStandard: customItem.standard,
        customEvidenceRequired: customItem.evidenceRequired,
        customObligationLevel: customItem.obligationLevel,
      }) as any),
      updatedAt: new Date().toISOString(),
    });
    setShowAddCustom(false);
    setCustomItem({ categoryCode: 'W', obligationLevel: 'Required' });
    toast.success('Custom compliance item added');
  }

  const categories = useMemo(() => {
    const codes = [...new Set(allItems.map(i => i.categoryCode))];
    return codes.map(code => ({ code, label: CATEGORY_META[code]?.label ?? code }));
  }, [allItems]);

  const countryLabel = (() => {
    switch ((country ?? '').toUpperCase()) {
      case 'GB': case 'UK': return 'United Kingdom (DWI / EA / HSE)';
      case 'AU': case 'NZ': return 'Australia (ADWG / EPA / Safe Work)';
      case 'US': case 'CA': return 'United States (SDWA / CWA / OSHA)';
      default:               return 'Generic (WHO / ISO)';
    }
  })();

  return (
    <div className="space-y-4">

      {/* Jurisdiction banner */}
      <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-sky-500/5 border border-sky-500/20">
        <Info className="w-4 h-4 text-sky-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-bold text-sky-300">Compliance framework: </span>
          <span className="text-xs text-sky-200/70">{countryLabel}</span>
        </div>
        <span className="text-[10px] text-slate-600 shrink-0">
          {WATER_BODY_TYPES.find(w => w.value === waterBodyType)?.label}
        </span>
      </div>

      {/* Summary dashboard */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[
          { label: 'Total',         value: stats.total,        color: '#64748b' },
          { label: 'Compliant',     value: stats.compliant,    color: '#10b981' },
          { label: 'In Progress',   value: stats.in_progress,  color: '#f59e0b' },
          { label: 'Not Started',   value: stats.not_started,  color: '#94a3b8' },
          { label: 'Non-Compliant', value: stats.non_compliant,color: '#ef4444' },
          { label: 'Mandatory Open',value: stats.mandatory_open, color: stats.mandatory_open > 0 ? '#ef4444' : '#10b981' },
        ].map(k => (
          <div key={k.label} className="p-2.5 rounded-xl bg-rb-elevated border border-slate-800/50 text-center">
            <div className="text-[9px] text-slate-500 uppercase tracking-wider leading-tight">{k.label}</div>
            <div className="text-xl font-bold font-mono mt-0.5" style={{ color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>Overall compliance progress</span>
          <span>{stats.total > 0 ? Math.round(((stats.compliant + stats.na) / stats.total) * 100) : 0}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden flex">
          {stats.total > 0 && <>
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(stats.compliant / stats.total) * 100}%` }} />
            <div className="h-full bg-amber-500/60 transition-all" style={{ width: `${(stats.in_progress / stats.total) * 100}%` }} />
            <div className="h-full bg-rose-500 transition-all" style={{ width: `${(stats.non_compliant / stats.total) * 100}%` }} />
            <div className="h-full bg-slate-700 transition-all" style={{ width: `${(stats.na / stats.total) * 100}%` }} />
          </>}
        </div>
      </div>

      {/* Filters + actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="text-xs bg-rb-elevated border border-slate-700/60 rounded-lg px-2.5 py-1.5 text-slate-300 focus:outline-none focus:border-sky-500">
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <select value={filterObligation} onChange={e => setFilterObligation(e.target.value)}
          className="text-xs bg-rb-elevated border border-slate-700/60 rounded-lg px-2.5 py-1.5 text-slate-300 focus:outline-none focus:border-sky-500">
          <option value="all">All Obligations</option>
          <option value="Mandatory">Mandatory</option>
          <option value="Required">Required</option>
          <option value="Advisory">Advisory</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="text-xs bg-rb-elevated border border-slate-700/60 rounded-lg px-2.5 py-1.5 text-slate-300 focus:outline-none focus:border-sky-500">
          <option value="all">All Statuses</option>
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="compliant">Compliant</option>
          <option value="non_compliant">Non-Compliant</option>
          <option value="na">N/A</option>
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setShowAddCustom(v => !v)}
            className="px-3 py-1.5 text-xs font-bold text-amber-400 bg-amber-500/10 border border-amber-800/40 hover:bg-amber-500/20 rounded-lg transition-colors flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add Custom Item
          </button>
          {dirty && (
            <button onClick={handleSave} disabled={saving}
              className="px-3 py-1.5 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-60">
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {/* Custom item form */}
      {showAddCustom && (
        <div className="p-4 rounded-2xl border border-amber-700/40 bg-amber-500/5 space-y-3">
          <h4 className="text-xs font-bold text-amber-300 uppercase tracking-wider">Add Custom Compliance Item</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Category</label>
              <select value={customItem.categoryCode ?? 'W'} onChange={e => setCustomItem(p => ({ ...p, categoryCode: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-xs text-slate-200 focus:outline-none focus:border-amber-500">
                {Object.entries(CATEGORY_META).map(([code, meta]) => (
                  <option key={code} value={code}>{meta.label}</option>
                ))}
                <option value="CX">Custom / Other</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Obligation Level</label>
              <select value={customItem.obligationLevel ?? 'Required'} onChange={e => setCustomItem(p => ({ ...p, obligationLevel: e.target.value as ObligationLevel }))}
                className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-xs text-slate-200 focus:outline-none focus:border-amber-500">
                <option value="Mandatory">Mandatory</option>
                <option value="Required">Required</option>
                <option value="Advisory">Advisory</option>
              </select>
            </div>
            <div className="col-span-2 space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Requirement *</label>
              <input value={customItem.requirement ?? ''} onChange={e => setCustomItem(p => ({ ...p, requirement: e.target.value }))}
                placeholder="Describe the compliance requirement…"
                className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-amber-500" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Applicable Standard</label>
              <input value={customItem.standard ?? ''} onChange={e => setCustomItem(p => ({ ...p, standard: e.target.value }))}
                placeholder="e.g. ISO 14001, local permit…"
                className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-amber-500" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Evidence Required</label>
              <input value={customItem.evidenceRequired ?? ''} onChange={e => setCustomItem(p => ({ ...p, evidenceRequired: e.target.value }))}
                placeholder="Document or action needed…"
                className="w-full px-3 py-2 rounded-xl bg-rb-elevated border border-slate-700/60 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-amber-500" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addCustomItem} className="px-4 py-2 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-xl transition-colors">Add Item</button>
            <button onClick={() => setShowAddCustom(false)} className="px-4 py-2 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 rounded-xl transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Compliance items */}
      <div className="space-y-2">
        {filteredItems.length === 0 && (
          <div className="p-8 text-center text-slate-600 rb-card">
            <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No items match the current filters</p>
          </div>
        )}

        {filteredItems.map(item => {
          const ov = complianceData.overrides[item.ref] ?? defaultOverride(item.ref);
          const status = STATUS_META[ov.status];
          const oblig  = OBLIGATION_META[item.obligationLevel];
          const catM   = CATEGORY_META[item.categoryCode] ?? { color: '#64748b', label: item.category };
          const isExpanded = expandedRef === item.ref;

          return (
            <div key={item.ref}
              className={`rounded-2xl border overflow-hidden transition-all ${
                ov.status === 'non_compliant' ? 'border-rose-800/50' :
                ov.status === 'compliant'     ? 'border-emerald-800/40' :
                ov.status === 'in_progress'   ? 'border-amber-800/40' :
                'border-slate-800/50'
              }`}>

              {/* Row header — always visible */}
              <button onClick={() => setExpandedRef(isExpanded ? null : item.ref)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-rb-elevated hover:bg-rb-elevated/80 transition-colors text-left">

                {/* Ref badge */}
                <span className="text-[10px] font-black px-2 py-0.5 rounded-lg shrink-0 font-mono"
                  style={{ background: `${catM.color}18`, color: catM.color, border: `1px solid ${catM.color}30` }}>
                  {item.ref}
                </span>

                {/* Requirement */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-slate-200 truncate">{item.requirement}</div>
                  <div className="text-[10px] text-slate-500 truncate hidden sm:block">{item.standard}</div>
                </div>

                {/* Obligation chip */}
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border shrink-0 hidden md:inline-flex ${oblig.color} ${oblig.bg} ${oblig.border}`}>
                  {item.obligationLevel}
                </span>

                {/* Status dropdown — stopPropagation so click doesn't toggle expand */}
                <div onClick={e => e.stopPropagation()}>
                  <select
                    value={ov.status}
                    onChange={e => updateOverride(item.ref, { status: e.target.value as ComplianceStatus })}
                    className={`text-[10px] font-bold px-2.5 py-1 rounded-lg border cursor-pointer
                               focus:outline-none focus:ring-1 focus:ring-sky-500 ${status.color} ${status.bg} ${status.border}`}
                  >
                    {(Object.entries(STATUS_META) as [ComplianceStatus, typeof STATUS_META[ComplianceStatus]][]).map(([val, meta]) => (
                      <option key={val} value={val}>{meta.label}</option>
                    ))}
                  </select>
                </div>

                <ChevronDown className={`w-3.5 h-3.5 text-slate-600 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>

              {/* Expanded detail panel */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-3 space-y-4 bg-rb-bg border-t border-slate-800/50">

                  {/* Standard + Evidence */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Applicable Standard / Regulation
                      </div>
                      <div className="text-xs text-slate-300 bg-rb-elevated rounded-xl px-3 py-2 border border-slate-800/50 leading-relaxed">
                        {item.standard || '—'}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                        <ClipboardList className="w-3 h-3" /> Evidence / Action Required
                      </div>
                      <div className="text-xs text-slate-300 bg-rb-elevated rounded-xl px-3 py-2 border border-slate-800/50 leading-relaxed">
                        {item.evidenceRequired || '—'}
                      </div>
                    </div>
                  </div>

                  {/* Editable fields */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-3 space-y-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        Evidence / Compliance Notes
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Record evidence held, action taken, document reference numbers, test results…"
                        value={ov.evidenceNotes}
                        onChange={e => updateOverride(item.ref, { evidenceNotes: e.target.value })}
                        className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-500 resize-none leading-relaxed"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Responsible Person</label>
                      <input
                        placeholder="Name / role / organisation"
                        value={ov.responsiblePerson}
                        onChange={e => updateOverride(item.ref, { responsiblePerson: e.target.value })}
                        className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Target Date</label>
                      <input
                        type="date"
                        value={ov.targetDate}
                        onChange={e => updateOverride(item.ref, { targetDate: e.target.value })}
                        className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Completed Date</label>
                      <input
                        type="date"
                        value={ov.completedDate}
                        onChange={e => updateOverride(item.ref, { completedDate: e.target.value, status: 'compliant' })}
                        className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
                      />
                    </div>
                  </div>

                  {/* Workflow next action */}
                  {ov.status === 'not_started' && item.obligationLevel === 'Mandatory' && (
                    <div className="flex items-start gap-2.5 rounded-xl bg-rose-500/5 border border-rose-500/20 px-3.5 py-2.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-rose-200/80">
                        <strong>Action needed:</strong> This is a Mandatory requirement. Assign a responsible person, set a target date, and gather evidence to move to <em>In Progress</em>.
                      </p>
                    </div>
                  )}
                  {ov.status === 'in_progress' && (
                    <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-2.5">
                      <Info className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-200/80">
                        <strong>Next step:</strong> Upload or record the evidence above, then set <em>Completed Date</em> and change status to <em>Compliant</em>.
                      </p>
                    </div>
                  )}
                  {ov.status === 'compliant' && (
                    <div className="flex items-start gap-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 px-3.5 py-2.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-emerald-200/80">
                        Compliant — ensure evidence is retained for audit.
                        {ov.completedDate && ` Completed: ${new Date(ov.completedDate).toLocaleDateString('en-GB')}.`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ReservoirManagementPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentCustomer } = useSession();

  // Site comes from router state (navigate('/reservoir-management', { state: { site } }))
  // or falls back to null with an error prompt
  const site: CustomerSite | null = (location.state as any)?.site ?? null;

  const [config, setConfig] = useState<ReservoirConfig>(() => {
    if (!site) return defaultConfig('', '');
    return getReservoirConfig(site.id) ?? defaultConfig(site.id, site.customerId);
  });

  const [activeTab, setActiveTab] = useState<'physical' | 'aeration' | 'processes' | 'water_quality' | 'compliance_matrix'>('physical');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Influent TP/TN from session or blank
  const influentTp = useMemo(() => 5.0, []); // placeholder — could pull from dosing state
  const influentTn = useMemo(() => 40.0, []);

  // Auto-derive retention time when volume/inflow change
  const derivedRetention = useMemo(() => {
    if (config.annualInflowMl <= 0) return 0;
    return Math.round((config.volumeMl / (config.annualInflowMl / 365)));
  }, [config.volumeMl, config.annualInflowMl]);

  function upd(partial: Partial<ReservoirConfig>) {
    setConfig(prev => ({ ...prev, ...partial }));
    setDirty(true);
  }

  const handleSave = useCallback(async () => {
    if (!site) { toast.error('No site linked — navigate here from a site card'); return; }
    setSaving(true);
    try {
      const toSave: ReservoirConfig = {
        ...config,
        retentionTimeDays: derivedRetention || config.retentionTimeDays,
        updatedAt: new Date().toISOString(),
      };
      saveReservoirConfig(toSave);
      setConfig(toSave);
      setDirty(false);
      toast.success('Reservoir configuration saved');
    } finally {
      setSaving(false);
    }
  }, [config, site, derivedRetention]);

  const TABS = [
    { id: 'physical',          label: 'Physical Config',       icon: Database },
    { id: 'aeration',          label: 'Aeration Systems',      icon: Wind },
    { id: 'processes',         label: 'Equipment & Processes', icon: Layers },
    { id: 'water_quality',     label: 'Water Quality',         icon: Activity },
    { id: 'compliance_matrix', label: 'Compliance Matrix',     icon: ClipboardList },
  ] as const;

  const siteLabel = site ? `${site.name}${site.address ? ` — ${site.address}` : ''}` : 'No site selected';

  if (!site) {
    return (
      <div className="min-h-screen bg-rb-bg p-6 flex items-center justify-center">
        <div className="rb-card p-12 text-center max-w-md">
          <Waves className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <div className="text-base font-bold text-slate-400 mb-2">No Site Selected</div>
          <p className="text-sm text-slate-500 mb-5">Navigate here from a reservoir or lake site card in the Customer Site page.</p>
          <button onClick={() => navigate('/customer-site')}
            className="px-4 py-2 text-sm font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-xl transition-colors">
            Go to Customer Sites
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rb-bg p-4 md:p-6 space-y-5">

      <PageHeader
        icon={Waves}
        iconColor="text-cyan-400"
        title={`Reservoir: ${site.name}`}
        subtitle={siteLabel}
        badge={
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-800/40 capitalize">
            {site.siteCategory?.replace('_', ' ') ?? 'Reservoir'}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}
              className="px-3 py-1.5 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg transition-colors flex items-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Sites
            </button>
            {dirty && (
              <button onClick={handleSave} disabled={saving}
                className="px-3 py-1.5 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-60">
                <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
          </div>
        }
      />

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Volume',       value: fv(config.volumeMl, 1),        unit: 'ML',    color: '#06b6d4' },
          { label: 'Surface Area', value: fv(config.surfaceAreaHa, 1),   unit: 'ha',    color: '#10b981' },
          { label: 'Avg Depth',    value: fv(config.avgDepthM, 1),       unit: 'm',     color: '#8b5cf6' },
          { label: 'HRT',          value: String(derivedRetention || config.retentionTimeDays), unit: 'days', color: '#f59e0b' },
        ].map(k => (
          <div key={k.label} className="rb-section px-4 py-3 text-center">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">{k.label}</div>
            <div className="text-xl font-bold font-mono mt-0.5" style={{ color: k.color }}>{k.value}</div>
            <div className="text-[10px] text-slate-600">{k.unit}</div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-rb-elevated rounded-2xl border border-slate-800/50">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
                active ? 'bg-rb-bg text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'
              }`}>
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'physical' && (
        <SectionCard title="Physical Characteristics" icon={Database} iconColor="text-cyan-400">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <NumInput label="Volume (ML)" value={config.volumeMl} onChange={v => upd({ volumeMl: v })} step={0.1}
              tip="Total storage volume in megalitres (1 ML = 1,000,000 L)" />
            <NumInput label="Surface Area (ha)" value={config.surfaceAreaHa} onChange={v => upd({ surfaceAreaHa: v })} step={0.1}
              tip="Open water surface area in hectares" />
            <NumInput label="Max Depth (m)" value={config.maxDepthM} onChange={v => upd({ maxDepthM: v })} step={0.1} />
            <NumInput label="Average Depth (m)" value={config.avgDepthM} onChange={v => upd({ avgDepthM: v })} step={0.1}
              tip="Mean depth = Volume (m³) / Surface Area (m²)" />
            <NumInput label="Catchment Area (km²)" value={config.catchmentAreaKm2} onChange={v => upd({ catchmentAreaKm2: v })} step={1} />
            <NumInput label="Annual Inflow (ML/yr)" value={config.annualInflowMl} onChange={v => upd({ annualInflowMl: v })} step={1}
              tip="Used to calculate theoretical hydraulic retention time" />
          </div>

          <div className="mt-4 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-xs text-slate-400">
            Derived HRT: <strong className="text-amber-400">{derivedRetention || config.retentionTimeDays}</strong> days
            {' · '}
            Vol/Area ratio (mean depth check): <strong className="text-sky-400">
              {config.surfaceAreaHa > 0 ? fv((config.volumeMl * 1e6) / (config.surfaceAreaHa * 1e4), 1) : '—'} m
            </strong>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Water Body Type</label>
              <select value={config.waterBodyType} onChange={e => upd({ waterBodyType: e.target.value as ReservoirConfig['waterBodyType'] })}
                className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500">
                {WATER_BODY_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
              <p className="text-[10px] text-slate-600">{WATER_BODY_TYPES.find(w => w.value === config.waterBodyType)?.notes}</p>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Trophic Status</label>
              <select value={config.trophicStatus} onChange={e => upd({ trophicStatus: e.target.value as ReservoirConfig['trophicStatus'] })}
                className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500">
                {TROPHIC.map(t => <option key={t.status} value={t.status}>{t.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Thermal Stratification</label>
              <select value={config.stratification} onChange={e => upd({ stratification: e.target.value as ReservoirConfig['stratification'] })}
                className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500">
                <option value="well_mixed">Well Mixed</option>
                <option value="seasonal">Seasonal Stratification</option>
                <option value="permanent">Permanently Stratified</option>
              </select>
            </div>
          </div>

          <div className="mt-5">
            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Site-Specific Compliance Targets</h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <NumInput label="Target TP ≤" value={config.targetTpMgL} onChange={v => upd({ targetTpMgL: v })} step={0.001} min={0.001} unit="mg/L"
                tip="Phosphorus limit" />
              <NumInput label="Target TN ≤" value={config.targetTnMgL} onChange={v => upd({ targetTnMgL: v })} step={0.01} min={0.01} unit="mg/L"
                tip="Nitrogen limit" />
              <NumInput label="Target Chl-a ≤" value={config.targetChlaUgL} onChange={v => upd({ targetChlaUgL: v })} step={0.5} min={0.5} unit="µg/L"
                tip="Chlorophyll-a limit" />
              <NumInput label="Target DO ≥" value={config.targetDoMgL} onChange={v => upd({ targetDoMgL: v })} step={0.1} min={0.5} unit="mg/L"
                tip="Minimum dissolved oxygen" />
              <NumInput label="Target Turbidity ≤" value={config.targetNtuTurbidity} onChange={v => upd({ targetNtuTurbidity: v })} step={0.5} min={0.5} unit="NTU" />
            </div>
          </div>
        </SectionCard>
      )}

      {activeTab === 'aeration' && (
        <SectionCard title="Aeration Systems" icon={Wind} iconColor="text-cyan-400">
          <div className="mb-4 flex items-start gap-2.5 rounded-xl bg-sky-500/5 border border-sky-500/20 px-3.5 py-3">
            <Info className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[11px] text-sky-200/70">
                Aeration is standard treatment for reservoirs and lakes. It maintains dissolved oxygen, prevents anaerobic stratification, reduces phosphorus release from sediments, and controls algal/cyanobacterial growth.
                Recommended power density: <strong className="text-sky-300">1–8 W/m³</strong> depending on water body depth and trophic status.
              </p>
              <button
                onClick={() => navigate('/reservoir-aeration-spec', { state: { siteName: site?.name } })}
                className="mt-2 px-3 py-1.5 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-lg transition-colors flex items-center gap-1.5 w-fit">
                <Wind className="w-3.5 h-3.5" /> Design Diffused Aeration System
              </button>
            </div>
          </div>
          <AerationPanel config={config} onChange={newConfig => { setConfig(newConfig); setDirty(true); }} />
        </SectionCard>
      )}

      {activeTab === 'processes' && (
        <SectionCard title="Equipment & Process Trains" icon={Layers} iconColor="text-amber-400">
          <div className="mb-4 flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
            <Info className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/70">
              Build treatment processes for this site using any equipment from the reservoir treatment library.
              Processes can represent inflow treatment trains, in-lake dosing programs, offtake treatment, or monitoring systems.
            </p>
          </div>
          <ProcessTrainPanel siteId={site.id} customerId={site.customerId} />
        </SectionCard>
      )}

      {activeTab === 'water_quality' && (
        <SectionCard title="Water Quality Monitor" icon={Activity} iconColor="text-emerald-400">
          <CompliancePanel config={config} influentTp={influentTp} influentTn={influentTn} />
        </SectionCard>
      )}

      {activeTab === 'compliance_matrix' && (
        <SectionCard title="Compliance Matrix" icon={ClipboardList} iconColor="text-violet-400">
          <ComplianceMatrixPanel
            siteId={site.id}
            customerId={site.customerId}
            country={site.country ?? ''}
            waterBodyType={config.waterBodyType}
          />
        </SectionCard>
      )}

    </div>
  );
}
